import type Cookies from 'cookies'

import config from '@app/config'
import type { BaseServerContext } from '@app/serverMiddleware/AppServerContext'

import type { CookieStore, CookieStoreSetOptions } from './CookieStore'
import { applyCookieDomain } from './domain'
import { parseCookieHeader } from './parseCookieHeader'

type KoaSetOption = Cookies.SetOption

/**
 * Server-side {@link CookieStore} backed by the Koa request/response. Reads come
 * from a snapshot of the request `Cookie` header (Koa's `ctx.cookies.get` only
 * sees the request, never pending writes), and the snapshot is mutated on
 * set/remove so within-request get-after-set works. Writes go straight to
 * `ctx.cookies.set` — synchronously, before the returned Promise resolves, so the
 * `Set-Cookie` is queued onto the response even when the caller never awaits.
 */
export class ServerCookieStore implements CookieStore {
  private readonly snapshot: Map<string, string>

  constructor(private readonly ctx: BaseServerContext) {
    this.snapshot = parseCookieHeader(ctx.request.header.cookie ?? '')
  }

  get(name: string): Promise<string | undefined> {
    return Promise.resolve(this.snapshot.get(name))
  }

  set(name: string, value: string, options?: CookieStoreSetOptions): Promise<void> {
    this.ctx.cookies.set(name, value, this.toOptions(options))
    this.snapshot.set(name, value)
    return Promise.resolve()
  }

  remove(name: string, options?: CookieStoreSetOptions): Promise<void> {
    this.ctx.cookies.set(name, null, applyCookieDomain({ path: options?.path ?? '/', domain: options?.domain, overwrite: true }))
    this.snapshot.delete(name)
    return Promise.resolve()
  }

  private toOptions(options?: CookieStoreSetOptions): KoaSetOption {
    return applyCookieDomain({
      path: options?.path ?? '/',
      httpOnly: options?.httpOnly ?? false,
      secure: options?.secure ?? config.secureCookie,
      overwrite: true,
      domain: options?.domain,
      expires: options?.expires,
      maxAge: options?.maxAge,
      sameSite: normalizeSameSite(options?.sameSite),
    })
  }
}

const normalizeSameSite = (sameSite?: CookieStoreSetOptions['sameSite']): KoaSetOption['sameSite'] =>
  sameSite ? (sameSite.toLowerCase() as 'lax' | 'strict' | 'none') : undefined
